import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DollarSign, TrendingUp, Calendar, Download, Printer, Filter,
  CreditCard, Banknote, Building2, Users, FileText, ArrowUp, ArrowDown, FileSpreadsheet
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { reportsAPI, branchesAPI, bookingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import GregorianDatePicker from '../../components/GregorianDatePicker';

const IncomeReport = () => {
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [reportData, setReportData] = useState({
    totalIncome: 0,
    totalBookings: 0,
    paidAmount: 0,
    unpaidAmount: 0,
    cashPayments: 0,
    cardPayments: 0,
    insurancePayments: 0,
    avgBookingValue: 0,
    monthlyTrend: [],
    paymentMethodBreakdown: [],
    topTests: []
  });

  useEffect(() => {
    // Set default date range (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(lastDay.toISOString().split('T')[0]);

    // Fetch branches
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchesAPI.getAll({ limit: 100 });
      // paginatedResponse returns: { data: { items, total, page, limit } }
      const data = response.data?.data?.items || response.data?.items || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchReportData();
    }
  }, [dateFrom, dateTo, selectedBranch]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo
      };
      if (selectedBranch) params.branch_id = selectedBranch;

      const response = await reportsAPI.getIncomeReport(params);

      const data = response.data?.data;

      if (data) {
        // Backend returns snake_case: total_sales, total_bookings, etc.
        setReportData({
          totalIncome: data.summary?.total_sales || data.summary?.totalIncome || 0,
          totalBookings: data.summary?.total_bookings || data.summary?.totalBookings || 0,
          paidAmount: data.summary?.paidAmount || 0,
          unpaidAmount: data.summary?.unpaidAmount || (data.summary?.total_sales - data.summary?.paidAmount) || 0,
          cashPayments: data.summary?.cashPayments || 0,
          cardPayments: data.summary?.cardPayments || 0,
          insurancePayments: data.summary?.insurancePayments || 0,
          avgBookingValue: data.summary?.avgBookingValue || (data.summary?.total_sales / (data.summary?.total_bookings || 1)) || 0,
          monthlyTrend: data.monthlyTrend || [],
          paymentMethodBreakdown: data.paymentMethodBreakdown || [],
          topTests: []
        });
      }

    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('حدث خطأ أثناء جلب بيانات التقرير');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const [exporting, setExporting] = useState(false);

  const handleExportToExcel = async () => {
    setExporting(true);
    const loadingToast = toast.loading('جاري تجهيز ملف الإكسل...');
    
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo
      };
      if (selectedBranch) params.branch_id = selectedBranch;

      const response = await bookingsAPI.export(params);
      const bookings = response.data?.data || [];

      if (bookings.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('لا توجد حجوزات في الفترة المحددة');
        setExporting(false);
        return;
      }

      const statusMap = {
        'new': 'جديد',
        'pending': 'معلق',
        'sample_collected': 'تم التجميع',
        'in_progress': 'قيد المعالجة',
        'completed': 'مكتمل',
        'cancelled': 'ملغي'
      };

      const paymentStatusMap = {
        'unpaid': 'غير مدفوع',
        'partial': 'مدفوع جزئياً',
        'paid': 'مدفوع بالكامل'
      };

      const paymentMethodMap = {
        'cash': 'نقدي',
        'card': 'بطاقة',
        'cash_card': 'نقدي + بطاقة',
        'bank_transfer': 'تحويل بنكي',
        'insurance': 'تأمين',
        'tamara': 'تمارا',
        'tabby': 'تابي'
      };

      const headers = [
        'رقم الحجز',
        'الباركود',
        'تاريخ الحجز',
        'وقت الحجز',
        'اسم العميل',
        'رقم الجوال',
        'الفرع',
        'التحاليل',
        'المبلغ الإجمالي',
        'الخصم',
        'الضريبة',
        'المبلغ الصافي',
        'المبلغ المدفوع',
        'المتبقي',
        'طريقة الدفع',
        'حالة الدفع',
        'حالة الحجز',
        'الموظف'
      ];

      const rows = bookings.map(booking => {
        const testsArr = booking.tests?.filter(t => t.lab_test_id) || [];
        const tests = testsArr.length > 0
          ? testsArr.map(t => t.lab_test_id?.name_ar || t.lab_test_id?.name_en || '-').join(' | ')
          : '-';

        const bookingDate = new Date(booking.booking_date);
        
        return [
          booking.booking_no || '-',
          booking.barcode_no || '-',
          bookingDate.toLocaleDateString('en-GB'),
          bookingDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
          booking.customer_id?.full_name || '-',
          booking.customer_id?.phone || '-',
          booking.branch_id?.branch_name || '-',
          tests,
          (booking.total_amount || 0).toFixed(2),
          (booking.discount_amount || 0).toFixed(2),
          (booking.vat_amount || 0).toFixed(2),
          (booking.net_amount || 0).toFixed(2),
          (booking.paid_amount || 0).toFixed(2),
          ((booking.net_amount || 0) - (booking.paid_amount || 0)).toFixed(2),
          paymentMethodMap[booking.payment_method] || '-',
          paymentStatusMap[booking.payment_status] || '-',
          statusMap[booking.status] || '-',
          booking.created_by_user_id?.name || '-'
        ];
      });

      const totals = bookings.reduce((acc, b) => ({
        total: acc.total + (b.total_amount || 0),
        discount: acc.discount + (b.discount_amount || 0),
        vat: acc.vat + (b.vat_amount || 0),
        net: acc.net + (b.net_amount || 0),
        paid: acc.paid + (b.paid_amount || 0)
      }), { total: 0, discount: 0, vat: 0, net: 0, paid: 0 });

      const summaryRows = [
        [],
        ['', '', '', '', '', '', '', 'الإجماليات:', totals.total.toFixed(2), totals.discount.toFixed(2), totals.vat.toFixed(2), totals.net.toFixed(2), totals.paid.toFixed(2), (totals.net - totals.paid).toFixed(2)],
        [],
        ['═══════════════════════════════════════════════════════════════════════════════════'],
        ['تقرير الدخل - ملخص'],
        [`الفترة: من ${dateFrom} إلى ${dateTo}`],
        [`عدد الحجوزات: ${bookings.length}`],
        [`إجمالي الإيرادات: ${totals.net.toFixed(2)} ر.س`],
        [`إجمالي المدفوع: ${totals.paid.toFixed(2)} ر.س`],
        [`إجمالي المتبقي: ${(totals.net - totals.paid).toFixed(2)} ر.س`]
      ];

      const csvData = [headers, ...rows, ...summaryRows];

      const escapeCsvValue = (val) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csv = csvData.map(row => row.map(escapeCsvValue).join(',')).join('\n');
      
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
      link.download = `تقرير_الدخل_${dateFrom}_${dateTo}.csv`;
    link.click();

      toast.dismiss(loadingToast);
      toast.success(`تم تصدير ${bookings.length} حجز بنجاح`);
    } catch (error) {
      console.error('Export error:', error);
      toast.dismiss(loadingToast);
      toast.error('حدث خطأ أثناء تصدير البيانات');
    }
    setExporting(false);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">تقرير الدخل</h1>
              <p className="text-slate-500 text-sm mt-1 print:text-gray-600">
                من {new Date(dateFrom).toLocaleDateString('en-GB')} إلى {new Date(dateTo).toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>
          <div className="flex gap-2 print:hidden">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handlePrint}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
            >
              <Printer className="w-4 h-4" strokeWidth={2.5} />
              <span>طباعة</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: exporting ? 1 : 1.02 }}
              whileTap={{ scale: exporting ? 1 : 0.98 }}
              onClick={handleExportToExcel}
              disabled={exporting}
              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                exporting 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
              }`}
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  <span>جاري التصدير...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4" strokeWidth={2.5} />
                  <span>تصدير إكسل</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Date Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 print:hidden"
        >
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Branch Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الفرع</label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white"
                >
                  <option value="">جميع الفروع</option>
                  {branches.map(branch => (
                    <option key={branch._id} value={branch._id}>{branch.branch_name}</option>
                  ))}
                </select>
              </div>
              <GregorianDatePicker
                label="من تاريخ"
                  value={dateFrom}
                onChange={setDateFrom}
                maxDate={dateTo || undefined}
                />
              <GregorianDatePicker
                label="إلى تاريخ"
                  value={dateTo}
                onChange={setDateTo}
                minDate={dateFrom || undefined}
                />
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100/80 rounded-xl backdrop-blur-sm">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex items-center gap-1 text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                <TrendingUp className="w-3 h-3" />
                <span>+12%</span>
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">إجمالي الدخل</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{reportData.totalIncome.toFixed(2)} <span className="text-base font-normal text-gray-400">ر.س</span></p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-green-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100/80 rounded-xl backdrop-blur-sm">
                <Banknote className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">المبلغ المدفوع</p>
            <p className="text-3xl font-bold text-green-600 tracking-tight">{reportData.paidAmount.toFixed(2)} <span className="text-base font-normal text-green-400">ر.س</span></p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-red-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-red-100/80 rounded-xl backdrop-blur-sm">
                <CreditCard className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">المبلغ غير المدفوع</p>
            <p className="text-3xl font-bold text-red-600 tracking-tight">{reportData.unpaidAmount.toFixed(2)} <span className="text-base font-normal text-red-400">ر.س</span></p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-500" />

            <div className="relative z-10 flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100/80 rounded-xl backdrop-blur-sm">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">عدد الحجوزات</p>
            <p className="text-3xl font-bold text-gray-900 tracking-tight">{reportData.totalBookings}</p>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Trend */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">اتجاه الدخل الشهري</h3>
            {reportData.monthlyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="income" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>لا توجد بيانات كافية</p>
              </div>
            )}
          </motion.div>

          {/* Payment Methods */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">توزيع طرق الدفع</h3>
            {reportData.paymentMethodBreakdown.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={reportData.paymentMethodBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {reportData.paymentMethodBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => `${value.toFixed(2)} ر.س`}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {reportData.paymentMethodBreakdown.map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded" style={{ background: item.color }} />
                        <span className="text-xs text-gray-600">{item.name}</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{item.value.toFixed(2)} ر.س</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>لا توجد بيانات كافية</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Additional Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-6">إحصائيات إضافية</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">متوسط قيمة الحجز</p>
                <p className="text-xl font-bold text-gray-900">{reportData.avgBookingValue.toFixed(2)} ر.س</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-green-100 rounded-lg">
                <Banknote className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">الدفع النقدي</p>
                <p className="text-xl font-bold text-gray-900">{reportData.cashPayments.toFixed(2)} ر.س</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">دفع البطاقات</p>
                <p className="text-xl font-bold text-gray-900">{reportData.cardPayments.toFixed(2)} ر.س</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Print Styles */}
        <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:bg-white {
            background-color: white !important;
          }
          .print\\:text-gray-800 {
            color: #1f2937 !important;
          }
          .print\\:text-gray-600 {
            color: #4b5563 !important;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default IncomeReport;

